import { ChangeDetectionStrategy, Component, Input, OnInit, signal } from '@angular/core'
import { CommonModule } from '@angular/common'
import { toObservable, toSignal } from '@angular/core/rxjs-interop'
import { interval, map, Observable, startWith } from 'rxjs'
import { CountdownWidget } from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { GbPresenterColorResolverService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-color-resolver.service'
import { TranslateModule } from '@ngx-translate/core'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { AuctionsEntity } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.models'

interface TimeRemaining {
  days: number
  hours: number
  minutes: number
  seconds: number
  isExpired: boolean
}

@UntilDestroy()
@Component({
  selector: 'gb-presenter-widget-countdown',
  imports: [CommonModule, TranslateModule],
  templateUrl: './gb-presenter-widget-countdown.component.html',
  styleUrl: './gb-presenter-widget-countdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterWidgetCountdownComponent implements OnInit {
  private countdownWidgetSignal = signal<CountdownWidget | null>(null)
  countdownWidget$ = toObservable(this.countdownWidgetSignal)

  private auctionSignal = toSignal(this.selectedAuctionFacade.auction$, { initialValue: null })

  auction$: Observable<AuctionsEntity | null> = this.selectedAuctionFacade.auction$

  constructor(
    private colorResolver: GbPresenterColorResolverService,
    private selectedAuctionFacade: SelectedAuctionFacade,
  ) {}

  @Input() set countdownWidget(value: CountdownWidget) {
    this.countdownWidgetSignal.set(value)
  }

  get countdownWidget(): CountdownWidget | null {
    return this.countdownWidgetSignal()
  }

  timeRemaining$!: Observable<TimeRemaining>

  ngOnInit(): void {
    this.timeRemaining$ = interval(1000).pipe(
      startWith(0),
      map(() => this.calculateTimeRemaining()),
      untilDestroyed(this),
    )
  }

  private calculateTimeRemaining(): TimeRemaining {
    const widget = this.countdownWidget

    const endTimestamp = this.getEndTimestamp(widget)
    if (!endTimestamp) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true }
    }

    const now = Date.now()
    const diff = endTimestamp - now

    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true }
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((diff % (1000 * 60)) / 1000)

    return { days, hours, minutes, seconds, isExpired: false }
  }

  private getEndTimestamp(widget: CountdownWidget | null): number | null {
    if (!widget) return null

    if (widget.end_on_auction_schedule_close) {
      const auction = this.auctionSignal()
      const auctionEnd = auction?.scheduled_end || auction?.closing_time
      return auctionEnd ? new Date(auctionEnd).getTime() : null
    }

    return widget.end ? new Date(widget.end).getTime() : null
  }

  get backgroundColor(): string {
    return this.colorResolver.resolve(this.countdownWidget?.background_color) || 'transparent'
  }

  get textColor(): string {
    return (
      this.colorResolver.resolve(this.countdownWidget?.color) || this.colorResolver.resolvedGlobalColor() || '#000000'
    )
  }

  get fontFamily(): string {
    return this.countdownWidget?.font_family || this.colorResolver.globalFontFamily() || ''
  }

  get fontWeight(): string {
    return this.countdownWidget?.font_weight || ''
  }

  get textAlign(): string {
    return this.countdownWidget?.text_align || 'center'
  }

  get fontSize(): number {
    return (this.countdownWidget?.font_size ?? 48) / 8
  }

  get letterSpacing(): number {
    return (this.countdownWidget?.letter_spacing ?? 1) / 10
  }

  get borderWidth(): number {
    return (this.countdownWidget?.border_width || 0) / 10
  }

  get borderColor(): string {
    return this.colorResolver.resolve(this.countdownWidget?.border_color) || 'transparent'
  }

  get borderStyle(): string {
    const width = this.countdownWidget?.border_width || 0
    return width > 0 ? this.countdownWidget?.border_style || 'solid' : 'none'
  }

  get timeoutText(): string {
    return this.countdownWidget?.timeout_text || 'Countdown finished'
  }
}
